import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Modal, Form, Input, ModalHeader, ModalBody, ModalFooter, Row, Col, Card, CardBody, Container, FormGroup, Label, FormFeedback, Badge } from 'reactstrap';
import { FaPlus, FaEdit, FaTrash, FaRegBuilding, FaCubes, FaCheck, FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { categoryActions } from '../../sagas/categorySlice';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';

const CategorieList = () => {
    const dispatch = useDispatch();
    const categories = useSelector((state) => state.categories.list); // Adjust state path as needed
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [initForm, setForm] = useState({ id: null, code: '', libelle: '' });
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(categoryActions.list({query:searchTerm})); // Fetch categories on component mount
    }, [searchTerm]);


    const handleCloseModal = () => {
        setIsModalVisible(false);
        formik.resetForm();
        dispatch(categoryActions.list({query:''})); // Fetch categories on component mount
    };

    const toggleModal = () => {
        setIsModalVisible(!isModalVisible);
        setForm({ id: null, code: '', libelle: '' });
    };


    const handleEditCategory = (category) => {
        console.log(category)
        setForm({
            id: category.id,
            libelle: category.libelle
        })
        setIsModalVisible(true);
    };

    const formik = useFormik({
        initialValues: { ...initForm },
        enableReinitialize: true,
        validationSchema: Yup.object({
            libelle: Yup.string().required('Libelle is required'),
        }),
        onSubmit: (values) => {
            let payload = {
                data: {
                    id: values.id,
                    code: values.code,
                    libelle: values.libelle,
                },
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: 'Succès',
                        text: 'Catégorie enregistrés avec succès!',
                        confirmButtonText: 'OK',
                    });
                    handleCloseModal();
                }
            }
            if (values.id) {
                dispatch(categoryActions.update(payload));
            } else {
                dispatch(categoryActions.create(payload));
            }
        },
    });


    const handleAddCategory = () => {
        formik.handleSubmit();
    }

    const handleDeleteCategory = (id) => {

        Swal.fire({
            title: t('category.deleteConfirmTitle'),
            text: t('product.deleteConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {

                    let payload = {
                        id: id,
                        onSuccess: () => {
                            Swal.fire(t('category.deleted'), '', 'success');
                            dispatch(categoryActions.list({query:''}));
                        },
                        onError: (error) => {
                            Swal.fire({
                                icon: 'error',
                                title: t('product.deleteError'),
                                text: t('product.deleteErrorMessage')
                            });
                        }
                    }
                    dispatch(categoryActions.delete(payload));

                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: t('common.error'),
                        text: t('product.deleteError')
                    });
                }
            }
        });
    };

    const handleActiverCategory = (id) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le catégorie sera activé et pourra être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, activer !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: id,
                    onSuccess: () => {
                        Swal.fire(
                            'Activé !',
                            'Le catégorie a été activé avec succès.',
                            'success'
                        );
                        dispatch(categoryActions.list({query:''}));
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de l\'activation.',
                            'error'
                        );
                    }
                }
                dispatch(categoryActions.activate(payload));
            }
        });
    };

    return (
        <Container fluid>
            <Row className="mb-4" >
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaCubes className="text-primary me-2" />
                                {t('category.list')}
                            </h4>
                            <Button
                                color="primary"
                                onClick={() => toggleModal()} // Go back to the previous page
                                className="d-flex align-items-center"
                            >
                                <FaPlus className="me-2" />
                                {t('category.new')}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    <div className="filter-container mb-4">
                        <div className="input-group">
                            <span className="input-group-text bg-light">
                                <FaSearch />
                            </span>
                            <input
                                type="text"
                                placeholder="Rechercher par nom, email, téléphone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="form-control"
                            />
                        </div>
                    </div>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>{t('category.code')}</th>
                                <th>{t('category.label')}</th>
                                <th>{t('client.active')}</th>
                                <th>{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.length > 0 && categories.map((category) => (
                                <tr key={category.id}>
                                    <td>{category.code}</td>
                                    <td>{category.libelle}</td>
                                    <td>
                                        {category.actif ? (
                                            <Badge color="success">Actif</Badge>
                                        ) : (
                                            <Badge color="danger">Bloqué</Badge>
                                        )}
                                    </td>
                                    <td>
                                        <Button
                                            color="warning"
                                            size="sm"
                                            onClick={() => handleEditCategory(category)}
                                        >
                                            <FaEdit />
                                        </Button>{' '}
                                        {category.actif ? <Button color="danger" size="sm" className="px-2 py-1">
                                            <FaTrash size={12} onClick={() => handleDeleteCategory(category.id)} />
                                        </Button>
                                            :
                                            <Button
                                                className="ms-1"
                                                color="success"
                                                size="sm"
                                                title='Activer'
                                                onClick={() => handleActiverCategory(category.id)}
                                            >
                                                <FaCheck />
                                            </Button>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {categories.length === 0 && (
                        <div className="text-center mt-4">
                            <FaCubes size={50} className="text-muted" />
                            <h5 className="mt-2">{t('category.noCategories')}</h5>
                        </div>
                    )}

                </CardBody>
            </Card>
            <Modal isOpen={isModalVisible} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {formik.values.id ? t('category.edit') : t('category.new')}
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={formik.handleSubmit}>

                        <FormGroup>
                            <Label for="libelle">{t('category.label')}</Label>
                            <Input
                                type="text"
                                name="libelle"
                                id="libelle"
                                value={formik.values.libelle}
                                onChange={formik.handleChange}
                                invalid={formik.touched.libelle && Boolean(formik.errors.libelle)}
                            />
                            <FormFeedback>{formik.errors.libelle}</FormFeedback>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleAddCategory}>
                        {formik.values.id ? t('common.update') : t('common.save')}
                    </Button>{' '}
                    <Button color="secondary" onClick={toggleModal}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default CategorieList;
